import { Directive, ElementRef, inject, input, signal } from '@angular/core';

/**
 * Shows a tooltip only while the host element actually clips its own text, mirroring the
 * prototypes' `dsCellTips` rule: the bubble carries what the element does NOT say, so a fully
 * visible label never gets one.
 *
 * Pair it with `ZardTooltipDirective` on the same element and bind `tooltip()` to `[zTooltip]`
 * (an empty value makes the Zard tooltip skip opening):
 *
 * ```html
 * <span
 *   #tip="svzTruncatedTooltip"
 *   class="block truncate"
 *   [svzTruncatedTooltip]="row.systemName"
 *   [zTooltip]="tip.tooltip()"
 *   zPosition="top"
 * >
 *   {{ row.systemName }}
 * </span>
 * ```
 *
 * The measurement runs on hover/focus rather than on every change detection, so it costs one
 * layout read per pointer entry and never on a data-bound path.
 */
@Directive({
  selector: '[svzTruncatedTooltip]',
  standalone: true,
  exportAs: 'svzTruncatedTooltip',
  host: {
    '(mouseenter)': 'measure()',
    '(focus)': 'measure()',
  },
})
export class SvzTruncatedTooltipDirective {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Full text of the element. Surfaced only while the host clips it. */
  public readonly text = input<string | null | undefined>('', { alias: 'svzTruncatedTooltip' });

  /** Bind to `[zTooltip]`. Holds the text while the host is clipped, `''` otherwise. */
  public readonly tooltip = signal('');

  protected measure(): void {
    const el = this.elementRef.nativeElement;
    const clipped = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
    this.tooltip.set(clipped ? (this.text() ?? '').trim() : '');
  }
}
